import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FxProvider } from "@/context/FxContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { CompanyProvider, useCompany } from "@/context/CompanyContext";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "@/lib/notify";
import AppLayout from "@/layouts/AppLayout";
import ForcePasswordChangeDialog from "@/components/ForcePasswordChangeDialog";
import ErrorBoundary from "@/components/ErrorBoundary";

const AuthPage = lazy(() => import("@/pages/AuthPage"));
const CompanySelectPage = lazy(() => import("@/pages/CompanySelectPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AccountsPage = lazy(() => import("@/pages/AccountsPage"));
const ChequesPage = lazy(() => import("@/pages/ChequesPage"));
const ReceivablesPage = lazy(() => import("@/pages/ReceivablesPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const BudgetsPage = lazy(() => import("@/pages/BudgetsPage"));
const UpcomingPage = lazy(() => import("@/pages/UpcomingPage"));
const OverduePage = lazy(() => import("@/pages/OverduePage"));
const PaymentsPage = lazy(() => import("@/pages/PaymentsPage"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const MobileAuditPage = lazy(() => import("@/pages/MobileAuditPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));
const TeklifDetailPage = lazy(() => import("@/pages/TeklifDetailPage"));
const TeklifDeletionRequestsPage = lazy(() => import("@/pages/TeklifDeletionRequestsPage"));
const CategoriesPage = lazy(() => import("@/pages/CategoriesPage"));
const KademePage = lazy(() => import("@/pages/KademePage"));
const KademeDetailPage = lazy(() => import("@/pages/KademeDetailPage"));
const ProductionPage = lazy(() => import("@/pages/ProductionPage"));
const ProductionExclusionsPage = lazy(() => import("@/pages/ProductionExclusionsPage"));
const ProductionCalendarPage = lazy(() => import("@/pages/ProductionCalendarPage"));
const BayiSiparisVerPage = lazy(() => import("@/pages/BayiSiparisVerPage"));
const BayiSiparislerimPage = lazy(() => import("@/pages/BayiSiparislerimPage"));
const BayiSiparisleriAdminPage = lazy(() => import("@/pages/BayiSiparisleriAdminPage"));
const PalletsPage = lazy(() => import("@/pages/PalletsPage"));
const ArchiveCleanupPage = lazy(() => import("@/pages/ArchiveCleanupPage"));

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--nf-bg)]" role="status" data-testid="page-loading-state">
      <div className="nf-label">Yükleniyor…</div>
    </div>
  );
}

/**
 * Yetkisiz Erişim redirect'i.  Navigate render edildiği an bir toast
 * gösterir; kullanıcı URL üzerinden yasaklı sayfaya (örn. Şirket Müdürü'nün
 * /cekler'e) girmeye çalışırsa neden geri döndürüldüğünü bilir.
 */
function UnauthorizedRedirect({ to = "/", message = "Yetkisiz Erişim — bu sayfaya erişim izniniz yok" }) {
  const loc = useLocation();
  useEffect(() => {
    toast.error(message, { id: `unauth-${loc.pathname}`, duration: 2500 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Navigate to={to} replace />;
}

function Protected({ children, mobile = false, adminOnly = false, contractsOnly = false, kademeOnly = false, productionOnly = false, productionCalendarOnly = false, bayiOnly = false, dealerApproverOnly = false, shipperOnly = false, operatorOnly = false, palletAccess = false }) {
  const { user } = useAuth();
  const { loading, currentId, companies } = useCompany();

  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/giris" replace />;
  if (loading) return <LoadingScreen />;
  if (companies.length === 0 || !currentId) return <Navigate to="/sirket-sec" replace />;
  // Shipping keeps pallet workflows; only the calendar gets extra read access.
  if (user.role === "sevkiyat" && !shipperOnly && !productionCalendarOnly) return <Navigate to="/barkod-stok" replace />;
  // Operatör rolü: sadece /barkod-stok (Palet Oluştur + Etiket Basım).
  // Diğer tüm route'lar palet sayfasına redirect eder.
  if (user.role === "operator" && !operatorOnly) return <Navigate to="/barkod-stok" replace />;
  if (adminOnly && user.role !== "admin"
      && !(shipperOnly && user.role === "sevkiyat")
      && !(operatorOnly && user.role === "operator")
      && !(palletAccess && ["santiye", "satis", "sirket_muduru"].includes(user.role))) return <Navigate to="/" replace />;
  // Bayi: only /bayi/* routes
  if (user.role === "bayi" && !bayiOnly) return <Navigate to="/bayi/siparislerim" replace />;
  // Dealer-order approver-only pages (admin/santiye/sirket_muduru)
  if (dealerApproverOnly && !["admin", "santiye", "sirket_muduru"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  // Satış Personeli: /sozlesmeler + /uretim + /uretim-takvimi (read-only)
  if (user.role === "satis" && !contractsOnly && !productionOnly && !palletAccess) return <Navigate to="/sozlesmeler" replace />;
  // Kademe rolü sadece /kademe sayfalarına erişebilir.
  if (user.role === "kademe" && !kademeOnly) return <Navigate to="/kademe" replace />;
  // Site manager: production/kademe/dealer and scoped pallet operations.
  if (user.role === "santiye" && !productionOnly && !kademeOnly && !dealerApproverOnly && !palletAccess) {
    return <Navigate to="/uretim" replace />;
  }
  // Şirket Müdürü: sozlesmeler + teklifler + production + kademe + bayi-siparisleri
  // (Çekler erişimi kaldırıldı — bu rol artık finans modülünü göremez.)
  // Teklifler read-only olarak erişilebilir; Onay/Red butonları backend'de
  // require_admin ile korunuyor, frontend'de bu rol için gizli.
  if (user.role === "sirket_muduru") {
    const allowedPaths = ["/sozlesmeler", "/teklifler", "/uretim", "/uretim-takvimi", "/kademe", "/bayi-siparisleri", "/barkod-stok"];
    const path = window.location.pathname;
    const isAllowed = allowedPaths.some((p) => path === p || path.startsWith(p + "/"));
    if (!isAllowed) return <UnauthorizedRedirect to="/sozlesmeler" />;
  }

  return mobile ? children : <AppLayout>{children}</AppLayout>;
}

function CompanyGate() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/giris" replace />;
  return <CompanySelectPage />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <FxProvider>
              <CompanyProvider>
                <CurrencyProvider>
                  <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                    <Route path="/giris" element={<AuthPage />} />
                    <Route path="/sirket-sec" element={<CompanyGate />} />
                    <Route path="/mobil" element={<Protected mobile><MobileAuditPage /></Protected>} />
                    <Route path="/" element={<Protected><DashboardPage /></Protected>} />
                    <Route path="/hesaplar" element={<Protected><AccountsPage /></Protected>} />
                    <Route path="/cekler" element={<Protected><ChequesPage /></Protected>} />
                    <Route path="/alacaklar" element={<Protected><ReceivablesPage /></Protected>} />
                    <Route path="/giderler" element={<Protected><ExpensesPage /></Protected>} />
                    <Route path="/butceler" element={<Protected><BudgetsPage /></Protected>} />
                    <Route path="/odemeler" element={<Protected><UpcomingPage /></Protected>} />
                    <Route path="/nakit-akisi" element={<Protected><PaymentsPage /></Protected>} />
                    <Route path="/gecikmis-odemeler" element={<Protected><OverduePage /></Protected>} />
                    <Route path="/sozlesmeler" element={<Protected contractsOnly><ContractsPage /></Protected>} />
                    <Route path="/teklifler/:teklifId" element={<Protected contractsOnly><TeklifDetailPage /></Protected>} />
                    <Route path="/raporlar" element={<Protected><ReportsPage /></Protected>} />
                    <Route path="/kullanicilar" element={<Protected adminOnly><UsersPage /></Protected>} />
                    <Route path="/etkinlik-gunlugu" element={<Protected adminOnly><AuditLogPage /></Protected>} />
                    <Route path="/arsiv-temizligi" element={<Protected adminOnly><ArchiveCleanupPage /></Protected>} />
                    <Route path="/sistem-analizi" element={<Protected adminOnly><Navigate to="/arsiv-temizligi" replace /></Protected>} />
                    <Route path="/admin/teklif-silme-talepleri" element={<Protected adminOnly><TeklifDeletionRequestsPage /></Protected>} />
                    <Route path="/admin/uretim-disi-kalemler" element={<Protected adminOnly><ProductionExclusionsPage /></Protected>} />
                    <Route path="/ayarlar/kategoriler" element={<Protected><CategoriesPage /></Protected>} />
                    <Route path="/kademe" element={<Protected kademeOnly><KademePage /></Protected>} />
                    <Route path="/kademe/:assetId" element={<Protected kademeOnly><KademeDetailPage /></Protected>} />
                    <Route path="/uretim" element={<Protected productionOnly><ProductionPage /></Protected>} />
                    <Route path="/uretim-takvimi" element={<Protected productionOnly productionCalendarOnly><ProductionCalendarPage /></Protected>} />
                    <Route path="/bayi/siparis-ver" element={<Protected bayiOnly><BayiSiparisVerPage /></Protected>} />
                    <Route path="/bayi/siparislerim" element={<Protected bayiOnly><BayiSiparislerimPage /></Protected>} />
                    <Route path="/bayi-siparisleri" element={<Protected dealerApproverOnly><BayiSiparisleriAdminPage /></Protected>} />
                    <Route path="/barkod-stok" element={<Protected adminOnly shipperOnly operatorOnly palletAccess><PalletsPage /></Protected>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Suspense>
                  <ForcePasswordChangeDialog />
                  <Toaster position="top-right" richColors />
                </CurrencyProvider>
              </CompanyProvider>
            </FxProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
